/**
 * Slide-in drawer that hosts an activity-scoped AI Coach conversation.
 *
 * - The drawer never opens automatically; it is only mounted when the
 *   athlete clicks the AI Coach button on ActivityDetail.
 * - Sends only the activity id and the athlete's message to the backend.
 *   All authoritative metrics are grounded server-side.
 * - Supports multi-turn follow-up questions on the same conversation and
 *   distinguishes network / auth / server / validation failures.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, X } from "lucide-react";

import { askYourCoachAboutActivity } from "@/api/aiCoachApi";
import { CoachingActionCard } from "@/components/coaching/CoachingActionCard";
import {
  affectsActiveTrainingPlan,
  PROPOSAL_PARSE_STATE,
} from "@/lib/coachingActionGuard";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const DEGRADED_NOTICE =
  "AI interpretation is temporarily unavailable; the data shown is from your recorded activities.";

const INITIAL_PROMPT_BY_SPORT = {
  cycling: "Give me a debrief of this ride: how did it go, what went well, what could improve, and what should I focus on next?",
  running: "Give me a debrief of this run: pacing, effort, what went well, what to improve, and next training focus.",
  swimming: "Give me a debrief of this swim session based on the recorded metrics.",
  walking: "Give me a summary of this walk based on the recorded metrics.",
  hiking: "Give me a summary of this hike based on the recorded metrics.",
  other: "Give me a debrief of this session based on the recorded metrics.",
};

const _sportFamily = (sport = "", subtype = "") => {
  const t = `${sport} ${subtype}`.toLowerCase();
  if (/cycl|bike|ride|mtb|gravel/.test(t)) return "cycling";
  if (/run|jog|trail/.test(t)) return "running";
  if (/swim/.test(t)) return "swimming";
  if (/hik/.test(t)) return "hiking";
  if (/walk/.test(t)) return "walking";
  return "other";
};

export const ActivityCoachDrawer = ({
  open,
  onOpenChange,
  activityId,
  activitySport,
  activitySportSubtype,
  askCoach = askYourCoachAboutActivity,
}) => {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversationRevision, setConversationRevision] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialFetched, setInitialFetched] = useState(false);
  // Phase 4C: the last athlete turn's stable client_message_id. Reused by
  // ``handleRetry`` so the backend's per-(athlete, conversation,
  // client_message_id) idempotency window can replay the original turn
  // instead of double-charging the model / duplicating a transcript row when
  // the network dropped the successful response.
  const [pendingClientMessageId, setPendingClientMessageId] = useState(null);
  // Activity ids from the last comparison on this workout. Cleared when the
  // drawer closes, the activity changes, or a non-comparison answer returns.
  const [comparisonActivityIds, setComparisonActivityIds] = useState([]);
  const scrollRef = useRef(null);

  const family = _sportFamily(activitySport, activitySportSubtype);

  const generateClientMessageId = () => {
    const random = Math.random().toString(16).slice(2, 10).padStart(8, "0");
    const stamp = Date.now().toString(16).slice(-8).padStart(8, "0");
    return `actcoach-${stamp}${random}`;
  };

  const confirmCoachingAction = useCallback(async (confirmMessage) => {
    if (!activityId) {
      throw new Error("Activity is required");
    }
    const clientMessageId = generateClientMessageId();
    const response = await askCoach({
      activityId,
      message: confirmMessage,
      conversationId,
      clientMessageId,
      expectedConversationRevision:
        typeof conversationRevision === "number"
          ? conversationRevision
          : undefined,
    });
    if (typeof response?.conversationRevision === "number") {
      setConversationRevision(response.conversationRevision);
    } else if (typeof response?.raw?.conversation_revision === "number") {
      setConversationRevision(response.raw.conversation_revision);
    }
    if (response?.conversationId) {
      setConversationId(response.conversationId);
    }
    const intent = response?.coachingAction?.proposal?.intent;
    if (
      response?.materialActionTaken
      && affectsActiveTrainingPlan(intent, true)
    ) {
      notifyActivePlanChanged({ source: "activity-coach" });
    }
    return response;
  }, [activityId, askCoach, conversationId, conversationRevision]);

  const sendMessage = useCallback(async (text, options = {}) => {
    if (!text || !activityId) return;
    const isRetry = Boolean(options.isRetry);
    const clientMessageId = isRetry && pendingClientMessageId
      ? pendingClientMessageId
      : generateClientMessageId();
    setLoading(true);
    setError(null);
    if (!isRetry) {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: text, ts: Date.now() },
      ]);
      setPendingClientMessageId(clientMessageId);
    }
    try {
      const response = await askCoach({
        activityId,
        message: text,
        conversationId,
        clientMessageId,
        expectedConversationRevision:
          typeof conversationRevision === "number"
            ? conversationRevision
            : undefined,
        comparisonActivityIds,
      });
      const reply = response?.reply || response?.raw?.reply || "";
      const status = response?.raw?.status;
      const followUp = response?.raw?.follow_up_question;
      const nextConversationId =
        response?.conversationId || response?.raw?.conversation_id;
      if (nextConversationId) {
        setConversationId(nextConversationId);
      }
      if (typeof response?.conversationRevision === "number") {
        setConversationRevision(response.conversationRevision);
      } else if (typeof response?.raw?.conversation_revision === "number") {
        setConversationRevision(response.raw.conversation_revision);
      }
      const returnedIds = response?.raw?.comparison_activity_ids;
      if (Array.isArray(returnedIds)) {
        setComparisonActivityIds(
          returnedIds.filter((value) => typeof value === "string" && value),
        );
      }
      if (
        status === "MODEL_UNAVAILABLE"
        && response?.retryable
        && !reply
      ) {
        setError("AI Coach couldn't complete that response. Try again.");
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "coach",
          content: reply || "The coach did not return a reply.",
          status,
          degradedMode: response?.degradedMode === true,
          retryable: response?.retryable === true,
          followUp,
          confidence: response?.raw?.confidence,
          coachingAction: response?.coachingAction,
          confirmationRequired: response?.confirmationRequired === true,
          confirmationId: response?.confirmationId,
          materialActionTaken: response?.materialActionTaken === true,
          planChangePreview: response?.planChangePreview || null,
          trainingPlanDraft: response?.trainingPlanDraft || null,
          conversationRevisionAtTurn:
            typeof response?.conversationRevision === "number"
              ? response.conversationRevision
              : (typeof response?.raw?.conversation_revision === "number"
                ? response.raw.conversation_revision
                : null),
          ts: Date.now(),
        },
      ]);
      // A completed turn (fresh or idempotent replay) closes the window on
      // its pending client_message_id — the next athlete turn generates a
      // fresh one via handleSubmit / handleFetchInitial.
      setPendingClientMessageId(null);
    } catch (err) {
      setError(
        err?.aiCoachError?.message
          || "Coach is temporarily unavailable.",
      );
    } finally {
      setLoading(false);
    }
  }, [activityId, askCoach, comparisonActivityIds, conversationId, conversationRevision, pendingClientMessageId]);

  // Reset state when the drawer is closed or the activity changes. This
  // guarantees switching to a different activity never reuses another
  // activity's conversation identity.
  useEffect(() => {
    if (!open) return;
    setMessages([]);
    setConversationId(null);
    setConversationRevision(null);
    setError(null);
    setInput("");
    setInitialFetched(false);
    setPendingClientMessageId(null);
    setComparisonActivityIds([]);
  }, [open, activityId]);

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleFetchInitial = () => {
    if (initialFetched || loading) return;
    setInitialFetched(true);
    sendMessage(INITIAL_PROMPT_BY_SPORT[family] || INITIAL_PROMPT_BY_SPORT.other);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput("");
    sendMessage(trimmed);
  };

  const handleRetry = () => {
    setError(null);
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    // Phase 4C: reuse the SAME client_message_id and do NOT append a duplicate
    // user bubble. This is what makes idempotent retry actually idempotent:
    // if the backend already persisted the original turn (successful model
    // call, dropped network response), the replay path returns the stored
    // reply without a second model call or a duplicate transcript row.
    sendMessage(lastUser.content, { isRetry: true });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-dark-background border-l border-dark-border p-0 flex flex-col"
        data-testid="activity-coach-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-dark-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading text-xl uppercase tracking-wider text-dark-text-primary flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
              Activity Coach
            </SheetTitle>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close coach"
              className="text-dark-text-muted hover:text-dark-text-primary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] tracking-[0.25em] uppercase text-dark-text-muted mt-1">
            {activitySport || "Workout"} · Grounded in this activity's recorded data
          </p>
        </SheetHeader>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
          data-testid="activity-coach-messages"
        >
          {messages.length === 0 && !loading && (
            <div className="border border-dark-border bg-dark-surface-1 p-4 text-sm text-dark-text-secondary space-y-3">
              <p>
                Ask the coach about this specific activity — pacing, effort,
                what went well, or what to focus on next. The coach uses only
                the metrics recorded in this session.
              </p>
              <button
                type="button"
                onClick={handleFetchInitial}
                className="inline-flex items-center gap-2 border border-brand-primary/60 text-brand-primary hover:bg-brand-primary hover:text-black px-3 py-2 text-xs tracking-[0.25em] uppercase transition-colors"
                data-testid="activity-coach-initial-review"
              >
                Get initial review
              </button>
            </div>
          )}

          {messages.map((m, idx) => (
            <div
              key={idx}
              data-testid={`coach-message-${m.role}`}
              className={
                m.role === "user"
                  ? "border border-dark-border bg-dark-surface-2 p-3 text-sm text-dark-text-primary"
                  : "border border-brand-primary/40 bg-dark-surface-1 p-3 text-sm text-dark-text-primary"
              }
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-dark-text-muted mb-1">
                {m.role === "user" ? "You" : "Coach"}
                {m.role === "coach" && m.confidence
                  ? ` · ${m.confidence.toLowerCase()} confidence`
                  : ""}
              </div>
              <p className="whitespace-pre-wrap break-words">{m.content}</p>
              {m.role === "coach" && m.degradedMode && (
                <p
                  data-testid="activity-coach-degraded-notice"
                  className="mt-2 text-xs text-dark-text-muted"
                >
                  {DEGRADED_NOTICE}
                </p>
              )}
              {m.role === "coach" && m.followUp && (
                <button
                  type="button"
                  onClick={() => sendMessage(m.followUp)}
                  disabled={loading}
                  className="mt-3 text-xs text-brand-primary hover:underline text-left"
                >
                  Ask: {m.followUp}
                </button>
              )}
              {m.role === "coach"
                && m.coachingAction?.parseState === PROPOSAL_PARSE_STATE.VALID && (
                <CoachingActionCard
                  coachingAction={m.coachingAction}
                  confirmationRequired={m.confirmationRequired}
                  conversationId={conversationId}
                  conversationRevision={m.conversationRevisionAtTurn}
                  materialActionTaken={m.materialActionTaken}
                  planChangePreview={m.planChangePreview}
                  trainingPlanDraft={m.trainingPlanDraft}
                  onConfirm={confirmCoachingAction}
                  testId={`activity-coach-action-${idx}`}
                />
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-xs text-dark-text-muted">
              <Loader2 className="w-3 h-3 animate-spin" /> Coach is thinking…
            </div>
          )}

          {error && (
            <div
              role="alert"
              data-testid="activity-coach-error"
              className="border border-analytics-amber text-analytics-amber bg-analytics-amber/10 p-3 text-sm"
            >
              <p>{error}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-2 text-xs underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="border-t border-dark-border px-6 py-4 flex items-end gap-2"
        >
          <label htmlFor="activity-coach-input" className="sr-only">
            Ask your coach about this activity
          </label>
          <textarea
            id="activity-coach-input"
            data-testid="activity-coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            rows={2}
            disabled={loading}
            placeholder="Ask your coach about this activity…"
            className="flex-1 bg-dark-surface-2 border border-dark-border text-dark-text-primary text-sm px-3 py-2 resize-none focus:outline-none focus:border-brand-primary"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="activity-coach-send"
            className="border border-brand-primary/60 text-brand-primary hover:bg-brand-primary hover:text-black px-4 py-2 text-xs tracking-[0.25em] uppercase disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
            Send
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ActivityCoachDrawer;
